import type { User } from '../../../utils/loginUtils'
import {
  updateStorageItem,
  type storageProduct,
} from '../../../utils/storageUtils'
import { getMessage } from '../../../utils/messageUtils'
import { useState } from 'react'

import './StorageProductList.scss'
import StoragePopupContent from './StoragePopupContent'
import Popup from '../../PopupForm/Popup'

interface StorageProductListProps {
  user: User
  products: storageProduct[] | undefined
  isMobile: boolean
  reloadData: () => void
  openPopup: (message: string, children: React.ReactNode) => void
  closePopup: () => void
  setPopupOnOk: React.Dispatch<React.SetStateAction<() => void>>
  setIsLoading: (isLoading: boolean) => void
}

function StorageProductList({
  user,
  products,
  isMobile,
  reloadData,
  openPopup,
  closePopup,
  setPopupOnOk,
  setIsLoading,
}: StorageProductListProps) {
  const [errorMessage, setErrorMessage] = useState('')
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)

  function generateProductList() {
    if (!products) return null

    const deleteStorageProduct = async (
      product: storageProduct | undefined
    ) => {
      let newProduct: storageProduct | undefined = undefined
      if (product) {
        newProduct = { ...product, quantity: 0 }
      }
      setIsLoading(true)
      try {
        const result = (await updateStorageItem(
          user.id!,
          newProduct,
          undefined
        )) as {
          success: boolean
          error?: string
        }
        if (!result.success) {
          setErrorMessage(getMessage('save_error') + (result.error || ''))
          setErrorPopupOpen(true)
        } else {
          reloadData()
        }
      } finally {
        setIsLoading(false)
      }
    }

    return products.map((product: storageProduct) => {
      return (
        <div
          key={product.id}
          className={`list-entry ${isMobile ? 'list-entry-mobile' : ''} storage-product-entry`}
          onClick={() => {
            openPopup(
              product.product_name || '',
              <StoragePopupContent
                user={user}
                product={product}
                isMobile={isMobile}
                setOnOk={setPopupOnOk}
                reLoadData={reloadData}
                closePopup={closePopup}
                setIsLoading={setIsLoading}
              />
            )
          }}
        >
          <div className="storage-product-name">
            <span>{product.product_name}</span>
          </div>
          <div className="storage-product-qty">
            <span>{product.quantity}</span>
          </div>
          <div
            className="storage-product-delete"
            onClick={e => {
              e.stopPropagation()
              deleteStorageProduct(product)
            }}
          >
            <span>×</span>
          </div>
        </div>
      )
    })
  }

  if (!products || products.length === 0) {
    return null
  }

  return (
    <div className="product-list-body-container">
      {generateProductList()}
      <Popup
        isOpen={errorPopupOpen}
        message={errorMessage}
        isMobile={isMobile}
        onOk={() => setErrorPopupOpen(false)}
        onClose={() => setErrorPopupOpen(false)}
        hideCancelButton={true}
      />
    </div>
  )
}

export default StorageProductList
